import { Component, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from "rxjs";
import { AppState } from 'src/app/store/app.reducer';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { selectManeuverConfigs, selectManeuverNotifCreated, selectManeuverNotifDeleted, selectManeuverNotifUpdated } from 'src/app/store/event/event.selector';
import { Device, DeviceTechnicianRole } from 'src/app/model/device';
import { MailNotificationConfig } from 'src/app/model/mail-notification-config';
import { ManeuverNotificationConfig } from 'src/app/model/maneuver-notification-config';
import { EDeviceModel } from 'src/app/conf/device-models';
import * as EventActions from 'src/app/store/event/event.actions';
import moment from 'moment';
import { Tooltip } from 'primeng/tooltip';
import { StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { TranslateService } from '@ngx-translate/core';
import * as NotificationsActions from 'src/app/store/notifications/notifications.actions';
import { Notification, NotificationLevel } from 'src/app/model/notification';

@Component({
	selector: 'app-notifications-device-maneuvers',
	templateUrl: './notifications-device-maneuvers.component.html',
	providers: []
})

export class NotificationsDeviceManeuversComponent  implements OnInit, OnDestroy {
	@Input() device: Device;
	@Input() technicianDevicesManagement: boolean;
	@Input() supportReqTechnician: DeviceTechnicianRole; // 1: Open, 0: Close, 2: TechnicianOwner

	// Observables and subscriptions
	maneuverConfigs$: Observable<ManeuverNotificationConfig[] | undefined>;
	maneuverConfigsSub: Subscription | undefined;
	maneuverNotifUpdated$: Observable<boolean | undefined>;
	maneuverNotifUpdatedSub: Subscription | undefined;
	maneuverNotifCreated$: Observable<boolean | undefined>;
	maneuverNotifCreatedSub: Subscription | undefined;
	maneuverNotifDeleted$: Observable<boolean | undefined>;
	maneuverNotifDeletedSub: Subscription | undefined;

	manSub: Subscription;

	emailConfigs: MailNotificationConfig[];
	maneuverConfigs: ManeuverNotificationConfig;

	maneuverNotifUpdated: boolean;
	maneuverNotifCreated: boolean;
	maneuverNotifDeleted: boolean;

	// maneuversNotif = supportManeuverNotif;

	maneuversForm: FormGroup;
	maneuversInitialValues: {
		maneuversNum: number;
		limitDate: Date;
	};
	today = new Date();

	maneuversCheckboxValue: boolean;

	screenWidth: number;
	
	@ViewChild(Tooltip) tooltip!: Tooltip;

	showTooltip = true;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
		){
		this.initSelectors();
	}

	ngOnInit(): void {
		this.store.dispatch(EventActions.getManeuversNotifConfigs({ deviceId: this.device.Id }));
		this.subscribeAll();
		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	initSelectors(): void{
		// Store selectors
		this.maneuverConfigs$ = this.store.select(selectManeuverConfigs);
		this.maneuverNotifUpdated$ = this.store.select(selectManeuverNotifUpdated);
		this.maneuverNotifCreated$ = this.store.select(selectManeuverNotifCreated);
		this.maneuverNotifDeleted$ = this.store.select(selectManeuverNotifDeleted);
	}

	subscribeAll(): void {
		this.maneuverConfigsSub = this.maneuverConfigs$?.subscribe(
			(maneuverConfigs) => {
				this.maneuverConfigs = maneuverConfigs.find(config => config.DeviceId === this.device.Id);
				this.setupManeuvers();
				this.setManeuvers();
			}
		);
		this.maneuverNotifUpdatedSub = this.maneuverNotifUpdated$?.subscribe(
			(maneuverNotifUpdated) => {
				this.maneuverNotifUpdated = maneuverNotifUpdated;
				// Understand if we need @Output closeDialog
				// if (maneuverNotifUpdated) {
				// 	this.closeDialog();
				// }
			}
		);
		this.maneuverNotifCreatedSub = this.maneuverNotifCreated$?.subscribe(
			(maneuverNotifCreated) => {
				this.maneuverNotifCreated = maneuverNotifCreated;
				// if (maneuverNotifCreated) {
				// 	this.closeDialog();
				// }
			}
		);
		this.maneuverNotifDeletedSub = this.maneuverNotifDeleted$?.subscribe(
			(maneuverNotifDeleted) => {
				this.maneuverNotifDeleted = maneuverNotifDeleted;
				// if (maneuverNotifDeleted) {
				// 	this.closeDialog();
				// }
			}
		);
	}

	ngOnDestroy(): void {
		this.maneuverConfigsSub?.unsubscribe();
		this.maneuverNotifUpdatedSub?.unsubscribe();
		this.maneuverNotifCreatedSub?.unsubscribe();
		this.maneuverNotifDeletedSub?.unsubscribe();
		this.manSub?.unsubscribe();
	}

	getManeuversValid(): boolean {
		return this.maneuversCheckboxValue ? this.maneuversForm.valid : true;
	}

	isTechRdsClosed(): boolean {
		return this.supportReqTechnician === DeviceTechnicianRole.SUP_REQ_CLOSED;
	}

	setupManeuvers(): void {
		this.maneuversForm = new FormGroup({
			'maneuvers': new FormControl({value: false, disabled: false}),
			'maneuversNum': new FormControl({value: null, disabled: true}, [Validators.required, Validators.min(0)]),
			'limitDate': new FormControl({value: null, disabled: true}, [Validators.required]),
		});
		this.manSub = this.fManeuvers.maneuvers.valueChanges.subscribe(value => {
			if (value) { // abilitating ..
				this.fManeuvers.maneuversNum.enable();
				this.fManeuvers.limitDate.enable();
			} else { // disabilitating ..
				this.fManeuvers.maneuversNum.disable();
				this.fManeuvers.limitDate.disable();
			}
			this.maneuversCheckboxValue = value;
		});
		this.maneuversInitialValues = this.fManeuvers;
	}

	setManeuvers() {
		if (this.disableManeuvers()){
				this.fManeuvers.maneuvers.disable();
		}
		if (this.maneuverConfigs !== undefined){
			this.fManeuvers.maneuvers.setValue(true);
			this.fManeuvers.maneuversNum.setValue(this.maneuverConfigs?.Threshold);
			if (this.maneuverConfigs.LimitNotificationDate !== undefined){
				this.fManeuvers.limitDate.setValue(moment(this.maneuverConfigs.LimitNotificationDate).toDate());
			}
		}
	}

	showHideTooltip() {
		this.tooltip.activate();
	}

	disableManeuvers(): boolean{
		if (this.device.ModelId === EDeviceModel.MultiIO ||
			this.device.ModelId === EDeviceModel.IORS485 ||
			this.device.ModelId === EDeviceModel.GEN_IORS485 ||
			this.device.ModelId === EDeviceModel.RBE4 ||
			this.device.ModelId === EDeviceModel.RBE4030 ||
			this.device.ModelId === EDeviceModel.RBE4230 ||
			this.device.ModelId === EDeviceModel.GEN_RBE4 ||
			this.device.ModelId === EDeviceModel.RBE24 ||
			this.device.ModelId === EDeviceModel.RBE230 ||
			this.device.ModelId === EDeviceModel.RBE24230 ||
			this.device.ModelId === EDeviceModel.GEN_RBE24230){
			return true;
		}
		return false;
	}

	// selectManeuversNum() {
	// 	console.log('selectManNum');
	// }

	// selectDate() {
	// 	console.log('selectDate');
	// }

	hideSaveManeuvers(): boolean {
		if (this.isTechRdsClosed() && this.maneuversForm.get("maneuvers").value === true) {
			this.fManeuvers.maneuversNum.disable();
			this.fManeuvers.limitDate.disable();
			return true; // hide save, i cannot save active checkbox, just disactivated
		}
		return this.maneuversForm.pristine; // pristine true if the user has not yet changed the value in the UI
	}

	deletingManeuvers(): boolean {
			return this.maneuverConfigs?.Id && !this.fManeuvers.maneuvers;
	}

	creatingManeuvers(): boolean {
			return !this.maneuverConfigs && this.fManeuvers.maneuvers;
	}

	modifyManeuvers(): boolean {
			return !!this.maneuverConfigs &&
					(this.maneuverConfigs.Threshold !== this.fManeuvers.maneuversNum ||
					this.maneuverConfigs.LimitNotificationDate !== this.fManeuvers.limitDate);
	}

	submitManeuvers(): void {
		if (this.maneuverConfigs?.Id && !this.fManeuvers.maneuvers.value) {
				this.store.dispatch(EventActions.deleteManeuversNotifConfig({ configId: this.maneuverConfigs.Id }));
				return;
		}
		const date = new Date(this.fManeuvers.limitDate.value);
		const limitDate: Date = new Date(date.setHours(23, 59, 59));
		const config: ManeuverNotificationConfig = {
				Threshold: this.fManeuvers.maneuversNum.value,
				LimitNotificationDate: limitDate,
				DeviceId: this.device.Id,
		};
		if (this.maneuverConfigs !== undefined) {
				config.Id = this.maneuverConfigs.Id;
				this.store.dispatch(EventActions.updateManeuversNotifConfig({ config }));
		} else {
				this.store.dispatch(EventActions.createManeuversNotifConfig({ config }));
		}
	}

	isBeforeToday(date: Date): boolean {
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		const inputDate = new Date(date);
		inputDate.setHours(0, 0, 0, 0);

		return inputDate < today;
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.maneuversForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.maneuversForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (this.maneuversForm.pristine) { // UI is invariated
			const notification = new Notification("NO_EDIT_INFO", this.translate.instant("NO_EDIT_DESC"), NotificationLevel.Warning, '');
			this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
		} else { // UI is changed
			const valueDate = this.fManeuvers.limitDate.value;
			if (valueDate) {
				const date = new Date(valueDate);
				const limitDate: Date = new Date(date.setHours(23, 59, 59));
				if (this.isBeforeToday(limitDate)) {
					this.fManeuvers.limitDate.setErrors({ invalid: true });
				} else { // is fine
					this.fManeuvers.limitDate.setErrors(null);
				}
			}
			if (!this.getFormValidationErrors()) {
				this.submitManeuvers();
			}
		}
	}

	get fManeuvers(): any {
		return this.maneuversForm.controls;
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	col12Responsive() : string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	dynamicButtonWidth() : string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextDesktopTabletWidth() : string {
		return StandardPageLayout.dynamicInputTextDesktopTabletWidth(this.screenWidth,'w-18rem','w-20rem');
	}

	justifyMessagesNewValidator() : string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: NotificationsDeviceManeuversComponent["fManeuvers"], classType: string) : string {
		return StandardNewValidator.newShowErrors(type,classType,this.screenWidth);
	}

	showErrorRequiredMessage(type: NotificationsDeviceManeuversComponent["fManeuvers"]) : boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}
}
